import type { Variant } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import type {
  Quiz,
  QuizAnswer,
  QuizQuestion,
  QuizResult,
} from '@bulknutrients/bulk-shared-lib/interfaces/Quiz';
import * as schema from '@bulknutrients/bulk-shared-lib/lib/drizzle/forms/schema';
import { drizzle } from '@bulknutrients/bulk-shared-lib/lib/drizzle/instances';
import { getReviewCount } from '@bulknutrients/bulk-shared-lib/lib/postgres/forms';
import {
  getAllQuizSlugs,
  getQuestionByRef,
  getQuizBySlug,
  getResultByRef,
} from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/quiz';
import { client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { Button } from '@components/Button';
import { EmblaCarousel } from '@components/Carousels/Carousel';
import { Container } from '@components/Container';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { StarRating } from '@components/Product/StarRating';
import { ProductCardQuizResult } from '@components/ProductCard/ProductCard';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { EveryBodyTumbleWord } from '@components/WordTumbler/WordTumbler';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { Pool } from 'pg';
import { type ReactElement, useCallback, useEffect, useState } from 'react';

interface QuizPageProps {
  quiz: Quiz;
  count: number;
}

function QuestionView({
  currentQuestion,
  handleAnswerClick,
  loading,
  history,
  handleBack,
  handleRestart,
}: {
  currentQuestion: QuizQuestion;
  handleAnswerClick: (answer: QuizAnswer) => void;
  loading: boolean;
  history: QuizQuestion[];
  handleBack: () => void;
  handleRestart: () => void;
}) {
  return (
    <div className="text-center my-8 flex flex-col *:text-black *:dark:text-white">
      <h2 className={`m-0 ${currentQuestion.helpText ? 'mb-2' : 'mb-8'}`}>
        {currentQuestion.questionText}
      </h2>
      {currentQuestion.helpText && (
        <p className="mb-8 text-grey-dark dark:text-grey-light px-10">
          {currentQuestion.helpText}
        </p>
      )}
      <div className="space-y-4">
        {currentQuestion.answers.map((answer) => (
          <Button
            key={answer._key}
            onClick={() => handleAnswerClick(answer)}
            buttonStyle="secondary"
            className="w-full h-fit normal-case bg-transparent text-black dark:text-white border-black 
              dark:border-white hover:bg-orange/20 disabled:text-white 
              hover:dark:bg-orange active:bg-orange/20 active:dark:bg-orange"
          >
            {answer.answerText}
          </Button>
        ))}
      </div>
      <div className="mt-8 w-full flex flex-row gap-4 justify-center">
        <Button
          onClick={history.length > 0 ? handleBack : handleRestart}
          disabled={loading}
        >
          Back
        </Button>
        <Button
          onClick={handleRestart}
          buttonStyle="secondary"
          disabled={loading}
        >
          Start Over
        </Button>
      </div>
    </div>
  );
}

function ResultView({
  result,
  recommendedSkus,
  handleBack,
  handleRestart,
}: {
  result: QuizResult;
  recommendedSkus: Record<number, string> | undefined;
  handleBack: () => void;
  handleRestart: () => void;
}) {
  const [selectedSkus, setSelectedSkus] = useState(recommendedSkus);

  const onVariantChange = useCallback((productId: number, variant: Variant) => {
    setSelectedSkus((prev) =>
      prev?.[productId] === variant.sku
        ? prev
        : { ...prev, [productId]: variant.sku }
    );
  }, []);
  return (
    <div className="my-8 *:text-black *:dark:text-white">
      <div className="mx-auto max-w-2xl flex flex-col">
        <h2 className="m-0 mb-6 text-center">{result.title}</h2>
        {result.description && (
          <Paragraph className="mb-6" align="center">
            {result.description}
          </Paragraph>
        )}
      </div>
      {result.recommendedProducts && result.recommendedProducts.length > 0 && (
        <>
          <div className="mx-auto max-w-[920px]">
            <EmblaCarousel
              slides={result.recommendedProducts.map((product) => (
                <ProductCardQuizResult
                  key={product.entityId}
                  product={product}
                  location="quiz-result"
                  onVariantChange={onVariantChange}
                />
              ))}
            />
          </div>
          <div className="mx-auto max-w-2xl flex flex-col">
            <Paragraph className="my-4" align="center">
              {result.additionalContent}
            </Paragraph>
            {selectedSkus && result.recommendedProductIds?.length && (
              <>
                <Button
                  buttonStyle="primary"
                  className="w-fit mx-auto"
                  href={`/api/promos/free-shaker?skus=${Object.values(selectedSkus).join(',')}&product_ids=${result.recommendedProductIds.join(',')}`}
                >
                  Add all to cart
                </Button>
                <Paragraph align="center" className="text-sm mb-4 font-bold">
                  ...and we'll include a free shaker with your order!
                </Paragraph>
              </>
            )}
          </div>
        </>
      )}
      <div className="mx-auto max-w-2xl flex flex-col">
        <HorizontalDivider className="mt-0 mb-6" />
        <div className="flex gap-4 justify-center">
          <Button onClick={handleBack}>Back</Button>
          <Button onClick={handleRestart} buttonStyle="secondary">
            Start Over
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function QuizPage({ quiz, count }: QuizPageProps): ReactElement {
  const [currentQuestion, setCurrentQuestion] = useState<QuizQuestion | null>(
    null
  );
  const [history, setHistory] = useState<QuizQuestion[]>([]);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [recommendedSkus, setRecommendedSkus] =
    useState<Record<number, string>>();
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Ensure hydration completes before rendering
  useEffect(() => setIsHydrated(true), []);

  const handleAnswerClick = async (answer: QuizAnswer) => {
    // Add current question to history before moving forward
    if (currentQuestion) {
      setHistory([...history, currentQuestion]);
    }

    setLoading(true);

    try {
      if ((window as any)?._learnq) {
        (window as any)?._learnq.push([
          'track',
          'Quiz Interaction',
          {
            Question: currentQuestion?.questionText,
            Answer: answer.answerText,
          },
        ]);
      }
    } catch {
      console.error(
        'Unable to send quiz info to Klaivyo: ',
        currentQuestion?.questionText,
        answer.answerText
      );
    }

    try {
      if (answer.nextStep.resultRef) {
        // Fetch and show result
        const fetchedResult: QuizResult & { recommendedProductIds?: number[] } =
          await client.fetch(getResultByRef, {
            ref: answer.nextStep.resultRef,
          });

        // Fetch full product data with BigCommerce pricing
        if (fetchedResult.recommendedProductIds?.length) {
          const products = await fetch(
            `/api/product?product_ids=${fetchedResult.recommendedProductIds.join(',')}`,
            {
              method: 'GET',
            }
          ).then((res) => res.json().then((data) => data.data as any[]));
          fetchedResult.recommendedProducts = products;
        }

        setResult(fetchedResult);

        const skusById = (fetchedResult.recommendedProducts ?? []).reduce<
          Record<number, string>
        >((acc, p) => {
          const sku = p.variants.find(
            (v) => v.variantId === p.defaultVariantId
          )?.sku;
          if (sku !== undefined) acc[p.entityId] = sku;
          return acc;
        }, {});

        setRecommendedSkus(skusById);
        setCurrentQuestion(null);
      } else if (answer.nextStep.questionRef) {
        // Fetch and move to next question
        const fetchedQuestion: QuizQuestion = await client.fetch(
          getQuestionByRef,
          {
            ref: answer.nextStep.questionRef,
          }
        );
        setCurrentQuestion(fetchedQuestion);
      }
    } catch {
      // Restore state if fetch failed
      if (currentQuestion && history.length > 0) {
        const newHistory = [...history];
        newHistory.pop();
        setHistory(newHistory);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // scroll to top as height changes
    window?.scrollTo({ top: 0 });

    if (result) {
      // Go back from result to last question
      setResult(null);
      const lastQuestion = history[history.length - 1];
      setCurrentQuestion(lastQuestion);
      return;
    }

    if (history.length > 0) {
      const newHistory = [...history];
      const previousQuestion = newHistory.pop();
      setHistory(newHistory);
      setCurrentQuestion(previousQuestion || null);
    }
  };

  const handleStart = () => {
    setStarted(true);
    setCurrentQuestion(quiz.startingQuestion);
    setHistory([]);
    setResult(null);
  };

  const handleRestart = () => {
    // scroll to top as height changes
    window?.scrollTo({ top: 0 });
    setStarted(false);
    setCurrentQuestion(null);
    setHistory([]);
    setResult(null);
  };

  return (
    <>
      <Head>
        <title>{quiz.metaTitle || quiz.title}</title>
        <meta name="title" content={quiz.metaTitle || quiz.title} />
        <meta name="description" content={quiz.metaDescription || ''} />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/quiz/${quiz.slug.current}`}
        />
      </Head>

      <Container as="main" id="main-content">
        {!started && !result ? (
          <div className="mx-auto max-w-xl justify-self-center *:text-black *:dark:text-white">
            <h1 className="my-8 text-center not-italic">
              <EveryBodyTumbleWord />
            </h1>

            {quiz.content && !started && isHydrated && (
              <div className="*:text-center *:m-0 my-12">
                <PortableTextBlockRenderer content={quiz.content} />
              </div>
            )}
            <div className="flex flex-col items-center">
              <Button onClick={handleStart} buttonStyle="primary">
                Start Quiz
              </Button>
              <div className="flex flex-col items-center my-12 gap-2">
                <StarRating rating={5} size={20} className="gap-1" />
                <p>Over {count.toLocaleString()} 5 star reviews</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Start View */}
        {started && (
          <>
            {/* Question View */}
            {currentQuestion && (
              <div className="flex min-h-[450px] mx-auto max-w-xl justify-self-center">
                <QuestionView
                  currentQuestion={currentQuestion}
                  handleAnswerClick={handleAnswerClick}
                  loading={loading}
                  history={history}
                  handleBack={handleBack}
                  handleRestart={handleRestart}
                />
              </div>
            )}
            {/* Result View */}
            {result && (
              <ResultView
                result={result}
                recommendedSkus={recommendedSkus}
                handleBack={handleBack}
                handleRestart={handleRestart}
              />
            )}
          </>
        )}
      </Container>
    </>
  );
}

QuizPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await client.fetch(getAllQuizSlugs);
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const quiz: Quiz = await client.fetch(getQuizBySlug, {
    slug: params?.quiz,
  });

  if (!quiz) {
    return {
      notFound: true,
    };
  }

  if (
    !process.env.PGUSER ||
    !process.env.PGPASSWORD_FORMS ||
    !process.env.PGDATABASE_FORMS ||
    !process.env.PGHOST_FORMS
  ) {
    throw new Error('Database environment variables are not set');
  }
  const pool = new Pool({
    user: process.env.PGUSER,
    password: process.env.PGPASSWORD_FORMS,
    database: process.env.PGDATABASE_FORMS,
    host: process.env.PGHOST_FORMS,
    port: 5432,
  });
  const db = drizzle(pool, { schema });

  const { count } = await getReviewCount({
    db,
    stars: '5',
  });

  return {
    props: {
      quiz,
      count,
    },
  };
};
